import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import Modal from "../../../components/base/Modal";
import { Badge, Button, Input, Select } from "../../../components/ui";
import {
  useCentinelaCatalogs,
  useCentinelaTripCustody,
  useCentinelaUnitCustody,
  useCentinelaUsers,
} from "../../../features/centinela/queries";
import {
  useEndCentinelaTripCustody,
  useEndCentinelaUnitCustody,
  useUpdateCentinelaTripCustody,
  useUpdateCentinelaUnitCustody,
} from "../../../features/centinela/mutations";
import {
  CENTINELA_CUSTODY_THRESHOLDS,
  CENTINELA_UNIT_CUSTODY_THRESHOLDS,
} from "../../../features/centinela/types";
import type {
  CentinelaCustodyThreshold,
  CentinelaTripCustody,
  CentinelaUnitCustodyThreshold,
} from "../../../features/centinela/types";
import { useTripsList } from "../../../features/trips/queries";
import type { Unit } from "../../../store/admin/unitsStore";
import { useCentinelaDemo } from "../services/centinelaDemoMode";
import { buildDemoTrips } from "../services/centinelaMocks";
import {
  CUSTODY_THRESHOLD_OPTIONS,
  CUSTOM_CUSTODY_PROFILE,
  custodyUnitLabelKey,
  hasCustodyChanges,
  hasThresholdChanges,
  isPresetProfile,
  PERMANENT_CUSTODY_EXPIRY,
  mapCentinelaTripCustody,
  mapCentinelaUnitCustody,
  mapCustodyParameters,
  mapCustodyProfiles,
  toCustodyExpiry,
  toTripCustodyInput,
  withServedOption,
} from "../services/mapCentinelaCustody";
import { mapCentinelaUsers } from "../services/mapCentinelaUsers";
import CustodyAlertsCoverage, {
  TRIP_ALERT_SCOPES,
  UNIT_ALERT_SCOPES,
} from "./CustodyAlertsCoverage";
import RecipientPicker from "./RecipientPicker";
import TabLoader from "./TabLoader";

const ESCORTABLE_STATUSES = ["inProgress", "scheduled", "in_progress", "programado", "en_curso"];

const CUSTODY_DURATIONS = [12, 24, 48] as const;

const DEFAULT_EXPIRY = 24;

type CustodyDuration = (typeof CUSTODY_DURATIONS)[number];

type CustodyExpiryChoice = CustodyDuration | "permanent" | "custom";

type CustodyMode = "standalone" | "trip";

const STANDALONE_DEFAULTS = {
  speed_limit_kmh: 80,
  gps_timeout_min: 15,
} as const satisfies Record<CentinelaUnitCustodyThreshold, number>;


const HOUR_MS = 60 * 60 * 1000;

const toLocalInputValue = (date: Date): string =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

const resolveExpiry = (choice: CustodyExpiryChoice, custom: string): Date | null => {
  if (choice === "permanent") return null;
  if (choice !== "custom") return new Date(Date.now() + choice * HOUR_MS);

  const parsed = custom ? new Date(custom) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return null;

  return parsed.getTime() > Date.now() ? parsed : null;
};

export interface UnitCustodyModalProps {
  unit: Unit;
  deviceId: string | null;
  operatorName: string;
  underCustody: boolean;
  onClose: () => void;
}

export default function UnitCustodyModal({
  unit,
  deviceId,
  operatorName,
  underCustody,
  onClose,
}: UnitCustodyModalProps) {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();

  const [mode, setMode] = useState<CustodyMode>("standalone");
  const [expiryDraft, setExpiryDraft] = useState<CustodyExpiryChoice | null>(null);
  const [customExpiry, setCustomExpiry] = useState("");
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CentinelaTripCustody | null>(null);
  const { data } = useTripsList({ limit: 100 });
  const forceDemo = useCentinelaDemo();

  const custodyQuery = useCentinelaTripCustody(selectedTripId);
  const updateCustody = useUpdateCentinelaTripCustody();
  const endTripCustody = useEndCentinelaTripCustody();
  const catalogsQuery = useCentinelaCatalogs();

  const unitCustodyQuery = useCentinelaUnitCustody(deviceId);
  const updateUnitCustody = useUpdateCentinelaUnitCustody();
  const endUnitCustody = useEndCentinelaUnitCustody();

  const servedUnitCustody = useMemo(
    () => mapCentinelaUnitCustody(unitCustodyQuery.data),
    [unitCustodyQuery.data],
  );

  const usersQuery = useCentinelaUsers();
  const recipients = useMemo(
    () => mapCentinelaUsers(usersQuery.data, () => null).filter((user) => user.active),
    [usersQuery.data],
  );

  const [standaloneRecipients, setStandaloneRecipients] = useState<string[] | null>(null);
  const standaloneNotify = standaloneRecipients ?? servedUnitCustody?.notify_user_ids ?? [];

  const changeTripRecipients = (userIds: string[]) => {
    setDraft({ ...custody, notify_user_ids: userIds });
  };

  const alertTypes = useMemo(
    () => catalogsQuery.data?.alert_types ?? [],
    [catalogsQuery.data],
  );

  const tripAlertTypes = useMemo(
    () => alertTypes.filter((alert) => TRIP_ALERT_SCOPES.includes(alert.scope)),
    [alertTypes],
  );

  const blockedStandaloneAlerts = useMemo(() => {
    const notApplicable = servedUnitCustody?.alertas_no_aplicables ?? [];

    return alertTypes
      .filter(
        (alert) =>
          !UNIT_ALERT_SCOPES.includes(alert.scope) || notApplicable.includes(alert.id),
      )
      .map((alert) => alert.id);
  }, [alertTypes, servedUnitCustody]);

  const [standaloneDraft, setStandaloneDraft] = useState<Record<
    CentinelaUnitCustodyThreshold,
    number
  > | null>(null);

  const standalone =
    standaloneDraft ??
    ({
      speed_limit_kmh: servedUnitCustody?.speed_limit_kmh ?? STANDALONE_DEFAULTS.speed_limit_kmh,
      gps_timeout_min: servedUnitCustody?.gps_timeout_min ?? STANDALONE_DEFAULTS.gps_timeout_min,
    } satisfies Record<CentinelaUnitCustodyThreshold, number>);

  const standaloneTouched = standaloneDraft !== null;

  const standaloneProfileLabel = standaloneTouched
    ? CUSTOM_CUSTODY_PROFILE
    : servedUnitCustody?.profile || CUSTOM_CUSTODY_PROFILE;

  const standaloneProfileToSend = standaloneTouched
    ? CUSTOM_CUSTODY_PROFILE
    : servedUnitCustody?.profile.trim() || undefined;

  const custodyProfiles = useMemo(
    () => mapCustodyProfiles(catalogsQuery.data),
    [catalogsQuery.data],
  );

  const thresholdUnits = useMemo(() => {
    const served = mapCustodyParameters(catalogsQuery.data);
    return new Map(served.map((parameter) => [parameter.field, parameter.unit]));
  }, [catalogsQuery.data]);

  const thresholdLabel = (key: CentinelaCustodyThreshold, value: number) => {
    const unit = thresholdUnits.get(key) ?? "";
    const unitKey = custodyUnitLabelKey(unit);
    return unitKey ? t(unitKey, { value }) : `${value} ${unit}`.trim();
  };

  const standaloneLocked = Boolean(
    standaloneProfileToSend && isPresetProfile(custodyProfiles, standaloneProfileToSend),
  );

  const alertsOfProfile = (profileId: string | undefined) =>
    (profileId && custodyProfiles.find((profile) => profile.id === profileId)?.alerts) || null;

  const coveredStandaloneAlerts = alertsOfProfile(standaloneProfileToSend);

  const activeStandalone = servedUnitCustody?.vigente ? servedUnitCustody : null;

  const servedExpiryChoice = (): CustodyExpiryChoice | null => {
    const served = servedUnitCustody?.expiracion;
    if (!served) return null;
    if (served.unidad === "permanente") return "permanent";

    return served.unidad === "horas" &&
      CUSTODY_DURATIONS.includes(served.cantidad as CustodyDuration)
      ? (served.cantidad as CustodyDuration)
      : "custom";
  };

  const expiryChoice = expiryDraft ?? servedExpiryChoice() ?? DEFAULT_EXPIRY;

  const served = useMemo(
    () => mapCentinelaTripCustody(custodyQuery.data),
    [custodyQuery.data],
  );
  const custody = draft ?? served;

  const tripProfileLabel = hasThresholdChanges(served, custody)
    ? CUSTOM_CUSTODY_PROFILE
    : custody.profile || CUSTOM_CUSTODY_PROFILE;

  const tripThresholdsLocked = isPresetProfile(custodyProfiles, tripProfileLabel);

  const coveredTripAlerts = alertsOfProfile(tripProfileLabel);

  const trips = useMemo(() => {
    const rows = data?.data ?? [];
    return forceDemo ? buildDemoTrips(t) : rows;
  }, [data, forceDemo, t]);

  const availableTrips = trips
    .filter((trip) => ESCORTABLE_STATUSES.includes(trip.status_id ?? ""))
    .slice(0, 5);

  const pickTrip = (tripId: string) => {
    setMode("trip");
    setSelectedTripId(tripId);
    setDraft(null);
  };

  const pickStandalone = (choice: CustodyExpiryChoice) => {
    setMode("standalone");
    setExpiryDraft(choice);
  };

  const updateThreshold = (key: CentinelaCustodyThreshold, value: number) => {
    setDraft({ ...custody, [key]: value });
  };

  const isPermanent = expiryChoice === "permanent";
  const expiresAt = resolveExpiry(expiryChoice, customExpiry);
  const hasExpiry = isPermanent || Boolean(expiresAt);
  const canActivateStandalone = hasExpiry && standaloneNotify.length > 0;

  const tripBlocker =
    custody.notify_user_ids.length === 0
      ? "monitoreoIa.units.custodyModal.needRecipients"
      : null;

  const formatMoment = (value: Date) =>
    value.toLocaleString(i18n.language, {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });

  const expiryLabel = expiresAt ? formatMoment(expiresAt) : "";

  const servedExpiryLabel = activeStandalone?.expira_en
    ? formatMoment(new Date(activeStandalone.expira_en))
    : "";

  const updateStandaloneThreshold = (key: CentinelaUnitCustodyThreshold, value: number) => {
    setStandaloneDraft({ ...standalone, [key]: value });
  };

  const activateWithoutTrip = () => {
    if (!canActivateStandalone || !deviceId) return;

    const expiracion = isPermanent
      ? PERMANENT_CUSTODY_EXPIRY
      : expiresAt
        ? toCustodyExpiry(expiresAt)
        : null;

    if (!expiracion) return;

    updateUnitCustody
      .mutateAsync({
        deviceId,
        input: {
          expiracion,
          activa: true,
          ...(standaloneProfileToSend ? { profile: standaloneProfileToSend } : {}),
          ...(standaloneLocked
            ? {}
            : {
                speed_limit_kmh: standalone.speed_limit_kmh,
                gps_timeout_min: standalone.gps_timeout_min,
              }),
          notify_user_ids: standaloneNotify,
        },
      })
      .then(() => {
        toast.success(
          isPermanent
            ? t("monitoreoIa.units.custodyModal.standalonePermanentDone", { unit: unit.name })
            : t("monitoreoIa.units.custodyModal.standaloneDone", {
                unit: unit.name,
                date: expiryLabel,
              }),
        );
        onClose();
      })
      .catch(() => {
        toast.error(t("monitoreoIa.units.custodyModal.saveError"));
      });
  };

  const endStandaloneCustody = () => {
    if (!deviceId) return;

    endUnitCustody
      .mutateAsync(deviceId)
      .then(() => {
        toast.success(t("monitoreoIa.units.custodyModal.standaloneEnded", { unit: unit.name }));
        onClose();
      })
      .catch(() => {
        toast.error(t("monitoreoIa.units.custodyModal.endError"));
      });
  };

  const endSelectedTripCustody = () => {
    if (!selectedTripId) return;

    endTripCustody
      .mutateAsync(selectedTripId)
      .then(() => {
        toast.success(t("monitoreoIa.units.custodyModal.tripEnded"));
        onClose();
      })
      .catch(() => {
        toast.error(t("monitoreoIa.units.custodyModal.endError"));
      });
  };

  const activateForTrip = () => {
    const trip = availableTrips.find((row) => row.id === selectedTripId);
    if (!trip || !selectedTripId) return;

    updateCustody
      .mutateAsync({
        tripId: selectedTripId,
        input: toTripCustodyInput(served, custody, custodyProfiles),
      })
      .then(() => {
        toast.success(
          t("monitoreoIa.units.custodyModal.tripDone", {
            trip: trip.title ?? t("monitoreoIa.trips.untitled"),
          }),
        );
        onClose();
      })
      .catch(() => {
        toast.error(t("monitoreoIa.units.custodyModal.saveError"));
      });
  };

  return (
    <Modal
      title={t("monitoreoIa.units.custodyModal.title")}
      onClose={onClose}
      width="w-full max-w-5xl"
      content={
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-xl border border-line bg-subtle px-3 py-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-500/10">
              <i className="ri-shield-check-line text-primary-500" />
            </span>
            <p className="min-w-0 truncate text-xs text-content-secondary">
              {unit.name} · {operatorName}
            </p>
          </div>

          {underCustody ? (
            <p className="flex items-start gap-2 rounded-xl border border-warning/30 bg-warning/10 px-3 py-2 text-[11px] leading-relaxed text-warning">
              <i className="ri-alert-line mt-0.5 shrink-0 text-xs" />
              {activeStandalone
                ? activeStandalone.permanente
                  ? t("monitoreoIa.units.custodyModal.activePermanent")
                  : t("monitoreoIa.units.custodyModal.activeUntil", {
                      date: servedExpiryLabel,
                    })
                : t("monitoreoIa.units.custodyModal.alreadyUnderCustody")}
            </p>
          ) : (
            <p className="text-[11px] leading-relaxed text-content-faint">
              {t("monitoreoIa.units.custodyModal.singleCustodyHint")}
            </p>
          )}

          <div
            className={`rounded-xl border p-4 transition-colors ${
              mode === "standalone" ? "border-primary-500/40 bg-primary-500/5" : "border-line bg-card"
            }`}
          >
            <button
              type="button"
              onClick={() => setMode("standalone")}
              className="flex w-full items-start gap-3 text-left max-sm:min-h-11"
            >
              <span
                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 max-sm:h-5 max-sm:w-5 ${
                  mode === "standalone" ? "border-primary-500" : "border-line"
                }`}
              >
                {mode === "standalone" && <span className="h-2 w-2 rounded-full bg-primary-500" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-bold text-content">
                  {t("monitoreoIa.units.custodyModal.standaloneTitle")}
                </span>
                <span className="mt-0.5 block text-[11px] leading-relaxed text-content-secondary max-sm:text-xs">
                  {t("monitoreoIa.units.custodyModal.standaloneDescription")}
                </span>
              </span>
            </button>

            {mode === "standalone" && (
              <div className="mt-3 space-y-2">
                <p className="text-[11px] font-medium text-content-secondary">
                  {t("monitoreoIa.units.custodyModal.standaloneDurationLabel")}
                </p>

                <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:gap-1.5">
                  {CUSTODY_DURATIONS.map((hours) => (
                    <button
                      key={hours}
                      type="button"
                      onClick={() => pickStandalone(hours)}
                      className={`rounded-lg border px-2.5 py-1 text-[11px] font-medium transition-colors max-sm:min-h-11 max-sm:px-2 max-sm:text-xs ${
                        expiryChoice === hours
                          ? "border-primary-500 bg-primary-500 text-on-primary"
                          : "border-line bg-app text-content-secondary hover:bg-subtle"
                      }`}
                    >
                      {t("monitoreoIa.units.custodyModal.hours", { value: hours })}
                    </button>
                  ))}

                  <button
                    type="button"
                    onClick={() => pickStandalone("permanent")}
                    className={`rounded-lg border px-2.5 py-1 text-[11px] font-medium transition-colors max-sm:min-h-11 max-sm:px-2 max-sm:text-xs ${
                      isPermanent
                        ? "border-warning bg-warning text-white"
                        : "border-line bg-app text-content-secondary hover:bg-subtle"
                    }`}
                  >
                    {t("monitoreoIa.units.custodyModal.standalonePermanent")}
                  </button>

                  <button
                    type="button"
                    onClick={() => pickStandalone("custom")}
                    className={`rounded-lg border px-2.5 py-1 text-[11px] font-medium transition-colors max-sm:min-h-11 max-sm:px-2 max-sm:text-xs ${
                      expiryChoice === "custom"
                        ? "border-primary-500 bg-primary-500 text-on-primary"
                        : "border-line bg-app text-content-secondary hover:bg-subtle"
                    }`}
                  >
                    {t("monitoreoIa.units.custodyModal.standaloneCustom")}
                  </button>
                </div>

                {expiryChoice === "custom" && (
                  <Input
                    size="sm"
                    type="datetime-local"
                    label={t("monitoreoIa.units.custodyModal.standaloneCustomLabel")}
                    value={customExpiry}
                    min={toLocalInputValue(new Date(Date.now() + 60000))}
                    error={
                      customExpiry && !expiresAt
                        ? t("monitoreoIa.units.custodyModal.standaloneExpiryInvalid")
                        : undefined
                    }
                    onChange={(event) => setCustomExpiry(event.target.value)}
                  />
                )}

                {isPermanent ? (
                  <p className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-2.5 py-2 text-[11px] leading-relaxed text-warning">
                    <i className="ri-money-dollar-circle-line mt-0.5 shrink-0 text-xs" />
                    {t("monitoreoIa.units.custodyModal.standalonePermanentCost")}
                  </p>
                ) : expiresAt ? (
                  <p className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-[11px] text-content-secondary">
                    <i className="ri-time-line shrink-0 text-[10px]" />
                    {t("monitoreoIa.units.custodyModal.standaloneExpiresAt", { date: expiryLabel })}
                  </p>
                ) : null}

                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-[11px] font-medium text-content-secondary">
                      {t("monitoreoIa.units.custodyModal.profileLabel")}
                    </span>
                    <Badge tone="info" size="sm">
                      {standaloneProfileLabel}
                    </Badge>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                    {CENTINELA_UNIT_CUSTODY_THRESHOLDS.map((key) => (
                      <Select
                        key={key}
                        size="sm"
                        disabled={standaloneLocked}
                        label={t(`monitoreoIa.units.custodyModal.thresholds.${key}`)}
                        value={`${standalone[key]}`}
                        onChange={(event) =>
                          updateStandaloneThreshold(key, Number(event.target.value))
                        }
                        options={withServedOption(
                          CUSTODY_THRESHOLD_OPTIONS[key],
                          standalone[key],
                        ).map((value) => ({
                          value: `${value}`,
                          label: thresholdLabel(key, value),
                        }))}
                      />
                    ))}
                  </div>
                </div>

                <RecipientPicker
                  users={recipients}
                  selected={standaloneNotify}
                  loading={usersQuery.isLoading}
                  label={t("monitoreoIa.units.custodyModal.recipientsLabel")}
                  emptyLabel={t("monitoreoIa.units.custodyModal.recipientsEmpty")}
                  onChange={setStandaloneRecipients}
                />

                <CustodyAlertsCoverage
                  alerts={alertTypes}
                  covered={coveredStandaloneAlerts}
                  blockedIds={blockedStandaloneAlerts}
                />

                {hasExpiry && standaloneNotify.length === 0 ? (
                  <p className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-2.5 py-2 text-[11px] leading-relaxed text-warning">
                    <i className="ri-alert-line mt-0.5 shrink-0 text-xs" />
                    {t("monitoreoIa.units.custodyModal.needRecipients")}
                  </p>
                ) : null}

                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                  {activeStandalone ? (
                    <Button
                      variant="dangerSoft"
                      size="sm"
                      className="max-sm:min-h-11"
                      disabled={endUnitCustody.isPending}
                      loading={endUnitCustody.isPending}
                      onClick={endStandaloneCustody}
                      icon={<i className="ri-shield-cross-line" />}
                    >
                      {t("monitoreoIa.units.custodyModal.standaloneEnd")}
                    </Button>
                  ) : null}

                  <Button
                    size="sm"
                    className="max-sm:min-h-11"
                    disabled={!canActivateStandalone || !deviceId || updateUnitCustody.isPending}
                    loading={updateUnitCustody.isPending}
                    onClick={activateWithoutTrip}
                    icon={<i className="ri-shield-check-line" />}
                  >
                    {activeStandalone
                      ? t("monitoreoIa.units.custodyModal.standaloneUpdate")
                      : t("monitoreoIa.units.custodyModal.standaloneActivate")}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div
            className={`rounded-xl border p-4 transition-colors ${
              mode === "trip" ? "border-primary-500/40 bg-primary-500/5" : "border-line bg-card"
            }`}
          >
            <button
              type="button"
              onClick={() => setMode("trip")}
              className="mb-3 flex w-full items-start gap-3 text-left max-sm:min-h-11"
            >
              <span
                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 max-sm:h-5 max-sm:w-5 ${
                  mode === "trip" ? "border-primary-500" : "border-line"
                }`}
              >
                {mode === "trip" && <span className="h-2 w-2 rounded-full bg-primary-500" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-bold text-content">
                  {t("monitoreoIa.units.custodyModal.tripTitle")}
                </span>
                <span className="mt-0.5 block text-[11px] leading-relaxed text-content-secondary max-sm:text-xs">
                  {t("monitoreoIa.units.custodyModal.tripDescription")}
                </span>
              </span>
            </button>

            {mode !== "trip" ? null : availableTrips.length === 0 ? (
              <div className="py-3 text-center">
                <p className="mb-2 text-xs text-content-secondary">
                  {t("monitoreoIa.units.custodyModal.noTrips")}
                </p>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => {
                    onClose();
                    navigate("/monitoreo-ia/viajes");
                  }}
                >
                  {t("monitoreoIa.units.custodyModal.goToTrips")}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-[11px] font-medium text-content-secondary">
                  {t("monitoreoIa.units.custodyModal.pickTrip")}
                </p>

                <div className="grid gap-2 sm:grid-cols-2">
                {availableTrips.map((trip) => (
                  <button
                    key={trip.id}
                    type="button"
                    onClick={() => pickTrip(trip.id)}
                    className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors max-sm:min-h-16 max-sm:py-3 ${
                      selectedTripId === trip.id
                        ? "border-primary-500/40 bg-primary-500/10"
                        : "border-line bg-surface hover:bg-subtle"
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 max-sm:h-5 max-sm:w-5 ${
                        selectedTripId === trip.id ? "border-primary-500" : "border-line"
                      }`}
                    >
                      {selectedTripId === trip.id && (
                        <span className="h-2 w-2 rounded-full bg-primary-500" />
                      )}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12.5px] font-semibold text-content">
                        {trip.title ?? t("monitoreoIa.trips.untitled")}
                      </span>
                      <span className="block truncate text-[11px] text-content-secondary max-sm:text-xs">
                        {trip.route?.name ?? t("monitoreoIa.trips.noRoute")}
                      </span>
                    </span>

                    {trip.status?.name && (
                      <span className="shrink-0 rounded-full border border-line bg-card px-2 py-0.5 text-[10px] font-medium text-content-secondary">
                        {trip.status.name}
                      </span>
                    )}
                  </button>
                ))}
                </div>

                {selectedTripId ? (
                  custodyQuery.isLoading ? (
                    <TabLoader label={t("monitoreoIa.units.custodyModal.loading")} />
                  ) : (
                    <div className="space-y-3 rounded-xl border border-line bg-surface p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[11px] font-medium text-content-secondary">
                          {t("monitoreoIa.units.custodyModal.profileLabel")}
                        </span>
                        <Badge tone="info" size="sm">
                          {tripProfileLabel}
                        </Badge>
                      </div>

                      <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                        {CENTINELA_CUSTODY_THRESHOLDS.map((key) => (
                          <Select
                            key={key}
                            size="sm"
                            disabled={tripThresholdsLocked}
                            label={t(`monitoreoIa.units.custodyModal.thresholds.${key}`)}
                            value={`${custody[key]}`}
                            onChange={(event) => updateThreshold(key, Number(event.target.value))}
                            options={withServedOption(
                              CUSTODY_THRESHOLD_OPTIONS[key],
                              custody[key],
                            ).map((value) => ({
                              value: `${value}`,
                              label: thresholdLabel(key, value),
                            }))}
                          />
                        ))}
                      </div>

                      <RecipientPicker
                        users={recipients}
                        selected={custody.notify_user_ids}
                        loading={usersQuery.isLoading}
                        label={t("monitoreoIa.units.custodyModal.recipientsLabel")}
                        emptyLabel={t("monitoreoIa.units.custodyModal.recipientsEmpty")}
                        onChange={changeTripRecipients}
                      />

                      <CustodyAlertsCoverage
                        alerts={tripAlertTypes}
                        covered={coveredTripAlerts}
                      />
                    </div>
                  )
                ) : null}

                {selectedTripId && tripBlocker ? (
                  <p className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-2.5 py-2 text-[11px] leading-relaxed text-warning">
                    <i className="ri-alert-line mt-0.5 shrink-0 text-xs" />
                    {t(tripBlocker)}
                  </p>
                ) : null}

                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                  {selectedTripId && served.profile ? (
                    <Button
                      variant="dangerSoft"
                      size="sm"
                      className="max-sm:min-h-11"
                      disabled={endTripCustody.isPending}
                      loading={endTripCustody.isPending}
                      onClick={endSelectedTripCustody}
                      icon={<i className="ri-shield-cross-line" />}
                    >
                      {t("monitoreoIa.units.custodyModal.tripEnd")}
                    </Button>
                  ) : null}

                  <Button
                    size="sm"
                    className="max-sm:min-h-11"
                    disabled={!selectedTripId || Boolean(tripBlocker) || updateCustody.isPending}
                    loading={updateCustody.isPending}
                    onClick={activateForTrip}
                    icon={<i className="ri-shield-flash-line" />}
                  >
                    {hasCustodyChanges(served, custody)
                      ? t("monitoreoIa.units.custodyModal.activateWithChanges")
                      : t("monitoreoIa.units.custodyModal.activate")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}
